import type { DailyBonusCardItem } from '../utils/dailyBonusRewards.utils';
import { useMemo } from 'react';
import { useOpenScratchCard } from '@/hooks/useOpenScratchCard';
import { useGroupedRewardsQuery } from '@/queries/rewards/rewards.queries';
import {
  buildMockDailyBonusCards,
  getCardState,
  isGroupActive,
  isGroupClaimed,
  MOCK_REWARD_AMOUNT_SC,
  orderGroupsForRender,
} from '../utils/dailyBonusRewards.utils';

export type { DailyBonusCardItem };

export const useDailyBonusRewards = () => {
  // Single timestamp shared by all per-render window checks below.
  const nowMs = Date.now();
  const { data: rewardsGrouped = [], isLoading } = useGroupedRewardsQuery();
  const openScratchCard = useOpenScratchCard();

  // Daily-bonus first, bundle-bonus after, sorted by priority.
  const orderedGroups = useMemo(() => orderGroupsForRender(rewardsGrouped), [rewardsGrouped]);

  // Cards for render. Falls back to mock layout when the API has no groups (guest / pre-fetch).
  const cards = useMemo<DailyBonusCardItem[]>(() => {
    if (orderedGroups.length === 0) {
      return buildMockDailyBonusCards();
    }

    return orderedGroups.map((group) => {
      const bonusDays = Math.max(0, group.amount);
      const isLockedByAmount = bonusDays <= 0;
      const isActive = isGroupActive(group, nowMs);
      const isClaimed = isGroupClaimed(group, nowMs);
      // Only attach onClick when claim is genuinely possible — keeps the card disabled otherwise.
      const canClaim = isActive && !isClaimed && !isLockedByAmount;

      return {
        id: group.id,
        type: group.type,
        rewardAmount: MOCK_REWARD_AMOUNT_SC,
        bonusDays,
        state: getCardState(isClaimed, isLockedByAmount, isActive),
        onClick: canClaim ? () => openScratchCard(group.id) : undefined,
      };
    });
  }, [orderedGroups, nowMs, openScratchCard]);

  return { cards, isLoading };
};
